import React from 'react';
import { Provider } from 'react-redux';
import { Route, Routes, BrowserRouter, Navigate } from 'react-router-dom';
import { createGlobalStyle } from 'styled-components';
import '~/locales/i18next';
import { ThemeProvider, createTheme, StyledEngineProvider } from '@mui/material/styles';
import { isAdminPath } from '@functional';
import { reset } from 'styled-reset';
import { worker } from '~/mock/msw/browser';
import defaultStyle from '~/assets/styles/defaultStyle';
import timepicker from '~/assets/styles/timepicker';

import { setupStore } from '~/App/store';

import PageWrapper from '~/components/layouts/Common/PageWrapper';
import ErrorPageWrapper from '~/components/layouts/Common/ErrorPageWrapper';
import VariousError from '~/components/pages/Common/Error/VariousError';
import NotFoundErrorCommon from '~/components/pages/Common/Error/NotFound';
import SignupError from '~/components/pages/Common/Error/SignupError';

import Signup from '~/components/pages/User/Signup';
import SignupConfirm from '~/components/pages/User/Signup/Confirm';
import SignupCompleted from '~/components/pages/User/Signup/Completed';
import SignupVerifyCompleted from '~/components/pages/User/Signup/VerifyCompleted';
import Signin from '~/components/pages/User/Signin';
import Mypage from '~/components/pages/User/Mypage';
import SurveyRegisterGoogleForms from '~/components/pages/User/Survey/Register/GoogleForms';
import SurveyRegisterOriginalForm from '~/components/pages/User/Survey/Register/OriginalForm';
import SurveyRegisterOriginalFormManual from '~/components/pages/User/Survey/Register/OriginalForm/Manual';
import SurveyRegisterSelection from '~/components/pages/User/Survey/Register';
import Survey from '~/components/pages/User/Survey';
import SurveyApply from '~/components/pages/User/Survey/Apply';
import EpsilonResult from '~/components/pages/User/Survey/Apply/EpsilonResult';
import SurveyApplyCompleted from '~/components/pages/User/Survey/Apply/Completed';
import SurveyReapplyCompleted from '~/components/pages/User/Survey/Apply/ReapplyCompleted';
import PasswordResetRequest from '~/components/pages/User/PasswordReset/Request';
import PasswordResetSendMailCompleted from '~/components/pages/User/PasswordReset/SendMailCompleted';
import PasswordResetInput from '~/components/pages/User/PasswordReset/Input';
import PasswordResetCompleted from '~/components/pages/User/PasswordReset/Completed';
import WebAggregation from '~/components/pages/User/WebAggregation';
import Withdraw from '~/components/pages/User/Withdraw';
import WithdrawConfirm from '~/components/pages/User/Withdraw/Confirm';
import WithdrawCompleted from '~/components/pages/User/Withdraw/Completed';
import UserInquiry from '~/components/pages/User/Inquiry';

import AdminSignup from '~/components/pages/Admin/Signup';
import AdminSignupConfirm from '~/components/pages/Admin/Signup/Confirm';
import AdminSignupCompleted from '~/components/pages/Admin/Signup/Completed';
import AdminSignupVerifyCompleted from '~/components/pages/Admin/Signup/VerifyCompleted';
import AdminSignin from '~/components/pages/Admin/Signin';
import AdminMypage from '~/components/pages/Admin/Mypage';
import AdminSurvey from '~/components/pages/Admin/Survey';
import AdminUsers from '~/components/pages/Admin/Users';
import AdminPasswordResetRequest from '~/components/pages/Admin/PasswordReset/Request';
import AdminPasswordResetSendMailCompleted from '~/components/pages/Admin/PasswordReset/SendMailCompleted';
import AdminPasswordResetInput from '~/components/pages/Admin/PasswordReset/Input';
import AdminPasswordResetCompleted from '~/components/pages/Admin/PasswordReset/Completed';
import AdministratorsAndRoles from '~/components/pages/Admin/Administrator';
import { PALETTE, ADMIN_PALETTE, SPACING, TYPOGRAPHY } from '~/assets/styles/base';
import overrideMUI from '~/assets/styles/overrideMUI';

import { ROUTES } from '~/constants/routes';

// モックサーバー起動
if (process.env.REACT_APP_MSW_ENABLED === 'true') {
  worker.start();
}

const GlobalStyle = createGlobalStyle`
  ${reset};
  ${defaultStyle};
  ${timepicker};
`;

function App() {
  // HACK: chromeだとセッション中はstateが維持されるのでReloadしたらuseLocationのstateが空になるように設定
  window.history.replaceState({}, null);

  const theme = createTheme({
    palette: isAdminPath() ? ADMIN_PALETTE : PALETTE,
    spacing: SPACING,
    components: overrideMUI,
    typography: TYPOGRAPHY,
  });

  return (
    <>
      <GlobalStyle />
      <StyledEngineProvider injectFirst>
        <ThemeProvider theme={theme}>
          <Provider store={setupStore()}>
            <BrowserRouter>
              <Routes>
                {/* 404 accessed thru URL normally */}
                <Route
                  path="*"
                  element={
                    <PageWrapper>
                      <NotFoundErrorCommon />
                    </PageWrapper>
                  }
                />

                {/* Access homepage automatically redirect to /mypage  */}
                <Route path={ROUTES.COMMON.HOMEPAGE} element={<Navigate to="/mypage" />} />

                {/* 404 for navigate explicitly */}
                <Route
                  path={ROUTES.USER[404]}
                  element={
                    <ErrorPageWrapper>
                      <VariousError errorPageNum="404" />
                    </ErrorPageWrapper>
                  }
                />

                {/* 403 */}
                <Route
                  path={ROUTES.USER[403]}
                  element={
                    <ErrorPageWrapper>
                      <VariousError errorPageNum="403" />
                    </ErrorPageWrapper>
                  }
                />

                {/* 500 */}
                <Route
                  path={ROUTES.USER[500]}
                  element={
                    <ErrorPageWrapper>
                      <VariousError errorPageNum="500" />
                    </ErrorPageWrapper>
                  }
                />

                {/* 本登録リンクのエラー画面 */}
                <Route
                  path={ROUTES.USER.SIGNUP_ERROR}
                  element={
                    <ErrorPageWrapper>
                      <SignupError />
                    </ErrorPageWrapper>
                  }
                />

                {/* 仮登録 */}
                <Route
                  path={ROUTES.USER.SIGNUP}
                  element={
                    <PageWrapper>
                      <Signup />
                    </PageWrapper>
                  }
                />

                {/* 仮登録確認 */}
                <Route
                  path={ROUTES.USER.SIGNUP_CONFIRM}
                  element={
                    <PageWrapper>
                      <SignupConfirm />
                    </PageWrapper>
                  }
                />

                {/* 仮登録完了（本登録URLメール送信完了） */}
                <Route
                  path={ROUTES.USER.SIGNUP_COMPLETED}
                  element={
                    <PageWrapper>
                      <SignupCompleted />
                    </PageWrapper>
                  }
                />

                {/* 本登録検証 */}
                <Route
                  path={ROUTES.USER.SIGNUP_VERIFY_COMPLETED}
                  element={
                    <PageWrapper>
                      <SignupVerifyCompleted />
                    </PageWrapper>
                  }
                />

                {/* ログイン */}
                <Route
                  path={ROUTES.USER.SIGNIN}
                  element={
                    <PageWrapper>
                      <Signin />
                    </PageWrapper>
                  }
                />

                {/* マイページ（アンケート一覧） */}
                <Route
                  path={ROUTES.USER.MYPAGE}
                  element={
                    <PageWrapper requireAuth>
                      <Mypage />
                    </PageWrapper>
                  }
                />

                {/* アンケート作成方法選択 */}
                <Route
                  path={ROUTES.USER.SURVEY_REGISTER_TYPE_SELECTION}
                  element={
                    <PageWrapper requireAuth>
                      <SurveyRegisterSelection />
                    </PageWrapper>
                  }
                />

                {/* アンケート作成 (Google Forms) */}
                <Route
                  path={ROUTES.USER.SURVEY_REGISTER_GOOGLE_FORMS}
                  element={
                    <PageWrapper requireAuth>
                      <SurveyRegisterGoogleForms />
                    </PageWrapper>
                  }
                />

                {/* アンケート作成 (Original Form) */}
                <Route
                  path={ROUTES.USER.SURVEY_REGISTER_ORIGINAL_FORM}
                  element={
                    <PageWrapper requireAuth>
                      <SurveyRegisterOriginalForm />
                    </PageWrapper>
                  }
                />

                {/* アンケート作成 (Original Form: Manual) */}
                <Route
                  path={ROUTES.USER.SURVEY_REGISTER_ORIGINAL_FORM_MANUAL}
                  element={
                    <PageWrapper requireAuth>
                      <SurveyRegisterOriginalFormManual />
                    </PageWrapper>
                  }
                />

                {/* WEB集計画面 */}
                <Route
                  path={ROUTES.USER.WEB_AGGREGATION}
                  element={
                    <PageWrapper requireAuth>
                      <WebAggregation />
                    </PageWrapper>
                  }
                />

                {/* アンケート詳細（配信設定） */}
                <Route
                  path={ROUTES.USER.SURVEY}
                  element={
                    <PageWrapper requireAuth>
                      <Survey />
                    </PageWrapper>
                  }
                />

                {/* 申し込み（決済方法選択） */}
                <Route
                  path={ROUTES.USER.SURVEY_APPLY}
                  element={
                    <PageWrapper requireAuth>
                      <SurveyApply />
                    </PageWrapper>
                  }
                />

                {/* クレカ決済結果  */}
                <Route
                  path={ROUTES.USER.EPSILON_RESULT}
                  element={
                    <PageWrapper requireAuth>
                      <EpsilonResult />
                    </PageWrapper>
                  }
                />

                {/* 申し込み完了 */}
                <Route
                  path={ROUTES.USER.SURVEY_APPLY_COMPLETED}
                  element={
                    <PageWrapper requireAuth>
                      <SurveyApplyCompleted />
                    </PageWrapper>
                  }
                />

                {/* 申し込み再申請完了 */}
                <Route
                  path={ROUTES.USER.SURVEY_REAPPLY_COMPLETED}
                  element={
                    <PageWrapper requireAuth>
                      <SurveyReapplyCompleted />
                    </PageWrapper>
                  }
                />

                {/* パスワード再設定リクエスト */}
                <Route
                  path={ROUTES.USER.PASSWORD_RESET_REQUEST}
                  element={
                    <PageWrapper>
                      <PasswordResetRequest />
                    </PageWrapper>
                  }
                />

                {/* パスワード再設定メール送信完了 */}
                <Route
                  path={ROUTES.USER.PASSWORD_RESET_SEND_MAIL_COMPLETED}
                  element={
                    <PageWrapper>
                      <PasswordResetSendMailCompleted />
                    </PageWrapper>
                  }
                />

                {/* パスワード再設定パスワード入力 */}
                <Route
                  path={ROUTES.USER.PASSWORD_RESET_INPUT}
                  element={
                    <PageWrapper>
                      <PasswordResetInput />
                    </PageWrapper>
                  }
                />

                {/* パスワード再設定完了 */}
                <Route
                  path={ROUTES.USER.PASSWORD_RESET_COMPLETED}
                  element={
                    <PageWrapper>
                      <PasswordResetCompleted />
                    </PageWrapper>
                  }
                />

                {/* ユーザ自主退会 */}
                <Route
                  path={ROUTES.USER.WITHDRAW}
                  element={
                    <PageWrapper requireAuth>
                      <Withdraw />
                    </PageWrapper>
                  }
                />

                {/* ユーザ自主退会確認 */}
                <Route
                  path={ROUTES.USER.WITHDRAW_CONFIRM}
                  element={
                    <PageWrapper requireAuth>
                      <WithdrawConfirm />
                    </PageWrapper>
                  }
                />

                {/* ユーザ自主退会完了 */}
                <Route
                  path={ROUTES.USER.WITHDRAW_COMPLETED}
                  element={
                    <PageWrapper>
                      <WithdrawCompleted />
                    </PageWrapper>
                  }
                />

                {/* User Inquiry */}
                <Route
                  path={ROUTES.USER.INQUIRY}
                  element={
                    <PageWrapper requireAuth>
                      <UserInquiry />
                    </PageWrapper>
                  }
                />

                {/* ↓↓↓↓↓↓ ここから管理者 ↓↓↓↓↓↓ */}

                {/* 404 for navigate explicitly */}
                <Route
                  path={ROUTES.ADMIN[404]}
                  element={
                    <ErrorPageWrapper>
                      <VariousError errorPageNum="404" />
                    </ErrorPageWrapper>
                  }
                />

                {/* 403 */}
                <Route
                  path={ROUTES.ADMIN[403]}
                  element={
                    <ErrorPageWrapper>
                      <VariousError errorPageNum="403" />
                    </ErrorPageWrapper>
                  }
                />

                {/* 500 */}
                <Route
                  path={ROUTES.ADMIN[500]}
                  element={
                    <ErrorPageWrapper>
                      <VariousError errorPageNum="500" />
                    </ErrorPageWrapper>
                  }
                />

                {/* 仮登録 */}
                <Route
                  path={ROUTES.ADMIN.SIGNUP}
                  element={
                    <PageWrapper requireAuth>
                      <AdminSignup />
                    </PageWrapper>
                  }
                />

                {/* 仮登録確認 */}
                <Route
                  path={ROUTES.ADMIN.SIGNUP_CONFIRM}
                  element={
                    <PageWrapper requireAuth>
                      <AdminSignupConfirm />
                    </PageWrapper>
                  }
                />

                {/* 仮登録完了（本登録URLメール送信完了） */}
                <Route
                  path={ROUTES.ADMIN.SIGNUP_COMPLETED}
                  element={
                    <PageWrapper requireAuth>
                      <AdminSignupCompleted />
                    </PageWrapper>
                  }
                />

                {/* 本登録検証 */}
                <Route
                  path={ROUTES.ADMIN.SIGNUP_VERIFY_COMPLETED}
                  element={
                    <PageWrapper>
                      <AdminSignupVerifyCompleted />
                    </PageWrapper>
                  }
                />

                {/* ログイン */}
                <Route
                  path={ROUTES.ADMIN.SIGNIN}
                  element={
                    <PageWrapper>
                      <AdminSignin />
                    </PageWrapper>
                  }
                />

                {/* マイページ（アンケート一覧） */}
                <Route
                  path={ROUTES.ADMIN.MYPAGE}
                  element={
                    <PageWrapper requireAuth>
                      <AdminMypage />
                    </PageWrapper>
                  }
                />

                {/* アンケート詳細 */}
                <Route
                  path={ROUTES.ADMIN.SURVEY}
                  element={
                    <PageWrapper requireAuth>
                      <AdminSurvey />
                    </PageWrapper>
                  }
                />

                {/* ユーザ情報一覧 */}
                <Route
                  path={ROUTES.ADMIN.USERS}
                  element={
                    <PageWrapper requireAuth wideLayout>
                      <AdminUsers />
                    </PageWrapper>
                  }
                />

                {/* ユーザ情報一覧 */}
                <Route
                  path={ROUTES.ADMIN.ADMINISTRATORS}
                  element={
                    <PageWrapper requireAuth wideLayout>
                      <AdministratorsAndRoles />
                    </PageWrapper>
                  }
                />

                {/* パスワード再設定リクエスト */}
                <Route
                  path={ROUTES.ADMIN.PASSWORD_RESET_REQUEST}
                  element={
                    <PageWrapper>
                      <AdminPasswordResetRequest />
                    </PageWrapper>
                  }
                />

                {/* パスワード再設定メール送信完了 */}
                <Route
                  path={ROUTES.ADMIN.PASSWORD_RESET_SEND_MAIL_COMPLETED}
                  element={
                    <PageWrapper>
                      <AdminPasswordResetSendMailCompleted />
                    </PageWrapper>
                  }
                />

                {/* パスワード再設定パスワード入力 */}
                <Route
                  path={ROUTES.ADMIN.PASSWORD_RESET_INPUT}
                  element={
                    <PageWrapper>
                      <AdminPasswordResetInput />
                    </PageWrapper>
                  }
                />

                {/* パスワード再設定完了 */}
                <Route
                  path={ROUTES.ADMIN.PASSWORD_RESET_COMPLETED}
                  element={
                    <PageWrapper>
                      <AdminPasswordResetCompleted />
                    </PageWrapper>
                  }
                />
              </Routes>
            </BrowserRouter>
          </Provider>
        </ThemeProvider>
      </StyledEngineProvider>
    </>
  );
}

export default App;
